import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { Fragment } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import ControlPanelContainer from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelContainer';
import ControlPanelDivider from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelDivider';
import CaseDataManagerSchemaSelectHandlebarWrapper from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/CaseDataManagerSchemaSelectHandlebarWrapper';
import Divider from 'dashboard/components/Divider';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { Attribute, ModuleDataDisplay, Section } from 'dashboard/modules/DataDisplay';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { BASE } from 'design-system/variables/spacing';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema, JsonSchemaObject } from 'lib/json-schema';

const StyledInput = styled(Input)`
  height: ${({ theme }) => theme.element.height.xs};
  padding: ${BASE / 4}px ${Spacing[2]};
  width: ${BASE * 32};
  ${paragraphMixins.md};
`;

const HeadingRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${Spacing[2]};
  align-items: center;
`;

const SectionHeadingContainer = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[4]};
`;

const AttributeInputContainer = styled.div`
  padding: ${Spacing[2]} 0px;
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  grid-column-gap: ${Spacing[2]};
  align-items: center;
`;

const AttributesWell = styled.div`
  margin: ${Spacing[2]} 0;
  padding: ${Spacing[4]} ${Spacing[5]};
  background-color: ${Color.CoreSlate100};
  border-radius: ${BORDER_RADIUSES.rounded};
`;

interface SectionConfigurationRowProps {
  section: Section;
  sectionIndex: number;
  jsonSchema: JsonSchema;
  updateSection: (section: Section, sectionIndex: number) => void;
  removeSection: (sectionIndex: number) => void;
}

const SectionConfigurationRow = ({
  section,
  sectionIndex,
  jsonSchema,
  updateSection,
  removeSection,
}: SectionConfigurationRowProps) => {
  const updateSectionTitle = (title: string) => {
    updateSection(
      {
        ...section,
        title: title,
      },
      sectionIndex,
    );
  };

  const addAttribute = () => {
    const nextAttributes = [
      ...section.attributes,
      {
        label: '',
        value: '',
      },
    ];

    updateSection(
      {
        ...section,
        attributes: nextAttributes,
      },
      sectionIndex,
    );
  };

  const removeAttribute = (attributeIndex: number) => {
    const nextAttributes = [
      ...section.attributes.slice(0, attributeIndex),
      ...section.attributes.slice(attributeIndex + 1),
    ];

    updateSection(
      {
        ...section,
        attributes: nextAttributes,
      },
      sectionIndex,
    );
  };

  const updateAttributeLabel = (label: string, index: number) => {
    const nextAttributes = [...section.attributes];
    nextAttributes[index] = {
      ...nextAttributes[index],
      label: label,
    };

    updateSection(
      {
        ...section,
        attributes: nextAttributes,
      },
      sectionIndex,
    );
  };

  const updateAttributeValue = (value: string, index: number) => {
    const nextAttributes = [...section.attributes];
    nextAttributes[index] = {
      ...nextAttributes[index],
      value: value,
    };

    updateSection(
      {
        ...section,
        attributes: nextAttributes,
      },
      sectionIndex,
    );
  };

  const updateAttributeObscurability = (value: boolean, index: number) => {
    const nextAttributes = [...section.attributes];
    nextAttributes[index] = {
      ...nextAttributes[index],
      obscurable: value,
    };

    updateSection(
      {
        ...section,
        attributes: nextAttributes,
      },
      sectionIndex,
    );
  };

  const attributesConfiguration = section.attributes.map((attribute, attributeIndex) => {
    const handleUpdateAttributeLabel = (label: string) => {
      updateAttributeLabel(label, attributeIndex);
    };

    const handleUpdateAttributeValue = (value: string) => {
      updateAttributeValue(value, attributeIndex);
    };

    const handleUpdateAttributeObscurability = (value: boolean) => {
      updateAttributeObscurability(value, attributeIndex);
    };

    return (
      <Fragment key={`data-display-panel__section-${sectionIndex}--attribute-${attributeIndex}`}>
        <AttributeInputContainer>
          <StyledInput
            id={`data-display-panel__section-${sectionIndex}--attribute-${attributeIndex}-label`}
            type='text'
            onChange={(e) => {
              handleUpdateAttributeLabel(e.target.value);
            }}
            placeholder='Attribute label'
            value={attribute.label}
          />
          {/* NB: we only need the value, hence why onFieldSelect is a no-op */}
          <CaseDataManagerSchemaSelectHandlebarWrapper
            id={`data-display-panel__section-${sectionIndex}--attribute-${attributeIndex}-input`}
            jsonSchema={jsonSchema}
            inputValue={attribute.value}
            onValueChange={handleUpdateAttributeValue}
          />
          <Button
            type='button'
            variant='basic'
            width='icon'
            onClick={() => removeAttribute(attributeIndex)}
          >
            <TrashIcon />
          </Button>
          <Checkbox
            id={`data-display-panel__section-${sectionIndex}--attribute-${attributeIndex}-obscurable`}
            label={<Paragraph>Obscurable</Paragraph>}
            checked={attribute.obscurable ?? false}
            onChange={(e) => {
              handleUpdateAttributeObscurability(e.target.checked);
            }}
            containerProps={{
              onClick: (e) => {
                // NB: This is needed so that clicking on the label maps to the correct checkbox.
                e.stopPropagation();
              },
              style: {
                padding: '4px 0px',
              },
            }}
          />
        </AttributeInputContainer>
      </Fragment>
    );
  });

  const [isOpenedSectionPanel, { enable: openSectionPanel, disable: closeSectionPanel }] =
    useToggle(false);

  return (
    <Fragment key={`data-display-panel__section-${sectionIndex}`}>
      <SectionHeadingContainer>
        <HeadingRow>
          <Heading size='sm'>{section.title ?? `Section ${sectionIndex + 1}`}</Heading>
          <HStack>
            <Button
              id={`data-display-panel__section-${sectionIndex}--button-edit`}
              type='button'
              variant='basic'
              width='icon'
              onClick={openSectionPanel}
            >
              <EditIcon />
            </Button>
            <Button
              id={`data-display-panel__section-${sectionIndex}--button-remove`}
              type='button'
              variant='danger'
              width='icon'
              onClick={() => removeSection(sectionIndex)}
            >
              <TrashIcon />
            </Button>
          </HStack>
        </HeadingRow>
      </SectionHeadingContainer>
      <Spacer height={2} />
      {isOpenedSectionPanel && (
        <MaximizablePanel
          title='Edit Section'
          offsetElementId='control-panel'
          onOutsideClickAndEscPress={closeSectionPanel}
          outsideExceptionalIdPrefixes={['data-display-panel__section']}
        >
          <HeadingRow>
            <Label size='md' weight='bold'>
              Title
            </Label>
            <StyledInput
              id={`data-display-panel__section-${sectionIndex}--title`}
              type='text'
              onChange={(e) => {
                updateSectionTitle(e.target.value);
              }}
              placeholder='Enter section title'
              value={section.title}
            />
          </HeadingRow>
          <Spacer height={1} />
          <Divider />
          <Spacer height={1} />
          <AttributesWell>{attributesConfiguration}</AttributesWell>
          <Spacer height={2} />
          <Button
            id={`data-display-panel__section-${sectionIndex}--add-attribute`}
            onClick={addAttribute}
            data-test={`data-display-panel__section-${sectionIndex}--add-attribute`}
            variant='basic'
            type='button'
            width='button'
          >
            Add attribute
          </Button>
          <Spacer height={2} />
        </MaximizablePanel>
      )}
    </Fragment>
  );
};

interface DataDisplayPanelProps {
  module: ModuleDataDisplay;
  dataSchema: JsonSchemaObject;
  updateModule: (module: ModuleDataDisplay) => void;
}

const DataDisplayPanel = ({ module, dataSchema, updateModule }: DataDisplayPanelProps) => {
  const updateTitle = (title: string) => {
    updateModule({
      ...module,
      title: title,
    });
  };

  const updateSubtitle = (subtitle: string) => {
    updateModule({
      ...module,
      subtitle: subtitle,
    });
  };

  const updateSection = (section: Section, sectionIndex: number) => {
    const nextSections = [...module.sections];
    nextSections[sectionIndex] = section;

    updateModule({
      ...module,
      sections: nextSections,
    });
  };

  interface AddSectionParams {
    title?: string;
    attributes?: Attribute[];
  }

  const addSection = ({ title = 'Section title', attributes = [] }: AddSectionParams) => {
    const nextSections = [
      ...module.sections,
      {
        title: title,
        attributes:
          attributes.length > 0
            ? attributes
            : [
                {
                  label: '',
                  value: '',
                },
              ],
      },
    ];

    updateModule({
      ...module,
      sections: nextSections,
    });
  };

  // This is a bit of a hack for now, we'll want to come up with a more flexible way to auto-generate sections
  let fieldSchemaAttributes: Attribute[] = [];
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (dataSchema?.properties?.current_case?.properties?.fields?.properties) {
    fieldSchemaAttributes = Object.entries(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      dataSchema.properties.current_case.properties.fields.properties,
    ).map(([key, value]) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return { label: value.title || key, value: `{{current_case.fields.${key}}}` };
    });
  }

  const addSectionWithAllFields = () => {
    if (fieldSchemaAttributes.length > 0) {
      addSection({ attributes: fieldSchemaAttributes });
    }
  };

  const removeSection = (sectionIndex: number) => {
    const nextSections = [
      ...module.sections.slice(0, sectionIndex),
      ...module.sections.slice(sectionIndex + 1),
    ];

    updateModule({
      ...module,
      sections: nextSections,
    });
  };

  return (
    <>
      <ControlPanelContainer>
        <Heading size='sm'>Headers</Heading>
        <Spacer height={2} />
        <Label size='md' weight='bold'>
          Title
        </Label>
        <Input
          id='data-display-panel__input--title'
          type='text'
          onChange={(e) => {
            updateTitle(e.target.value);
          }}
          placeholder='Enter title'
          value={module.title}
        />
        <Spacer height={2} />
        <Label size='md' weight='bold'>
          Subtitle
        </Label>
        <Input
          id='data-display-panel__input--subtitle'
          type='text'
          onChange={(e) => {
            updateSubtitle(e.target.value);
          }}
          placeholder='Enter subtitle'
          value={module.subtitle}
        />
        <Spacer height={1} />
      </ControlPanelContainer>
      <ControlPanelDivider full={true} />
      <ControlPanelContainer>
        <Heading size='sm'>Section Configuration</Heading>
        <Spacer height={2} />
        {module.sections.map((section, sectionIndex) => (
          <SectionConfigurationRow
            key={sectionIndex}
            section={section}
            sectionIndex={sectionIndex}
            jsonSchema={dataSchema}
            updateSection={updateSection}
            removeSection={removeSection}
          />
        ))}
        <Button
          id='data-display-panel__add-section'
          onClick={() => addSection({})}
          data-test='data-display-panel__add-section'
          variant='basic'
          type='button'
          width='button'
        >
          Add section
        </Button>
        {fieldSchemaAttributes.length > 0 && (
          <>
            <Spacer height={1} />
            <Button
              id='data-display-panel__add-section-with-all-fields'
              variant='basic'
              type='button'
              width='button'
              onClick={addSectionWithAllFields}
            >
              Add section with all fields
            </Button>
          </>
        )}
      </ControlPanelContainer>
    </>
  );
};

export default DataDisplayPanel;
